<template>
  <b-modal :title="helper.trans('labels.deposit')"
           :modelValue="account !== null"
           header-class="text-capitalize"
           footer-class="d-flex justify-content-end"
           @update:modelValue="$event === false && (account = null)">
    <template #default>
      <LoaderWrapper ref="loader" class="h-100">
        <form v-if="account">
          <div class="row mb-2">
            <div class="col-sm-12">

              <div class="row mt-2">
                <div class="col-sm-12 d-flex align-items-center justify-content-center">
                  <AccountWidget :hide-insurance="store.getters.isMerchant" show-icon :account="account"></AccountWidget>
                </div>
              </div>

              <div class="row mt-3" v-if="wallet">
                <label class="col-sm-12 text-center">{{ helper.trans("labels.address.usdt") }}</label>
                <div class="col-sm-12 wallet-address p-3 mt-1" >
                  <div class="d-flex flex-row gap-2 justify-content-center">
                    <pre class="m-0" style="font-size: 100%"><code>{{ wallet.address }}</code></pre> <copy-button :value="wallet.address"></copy-button>
                  </div>
                  <div v-if="isAdmin || isOperator" class="d-flex flex-column justify-content-center align-items-center font-monospace mt-2">
                    <small>TRX: {{ walletsInfo ? numberFormatter.formatNumber(walletsInfo?.trx) : 'n/a' }}</small>
                    <small>USDT: {{ walletsInfo ? numberFormatter.formatNumber(walletsInfo?.usdt) : 'n/a' }}</small>
                  </div>
                </div>

              </div>
              <div v-else-if="wallet === false && isAdmin" class="d-flex align-items-center justify-content-center">
                <button @click.prevent="createWallet()" type="button" class="btn btn-outline-dark btn-sm text-capitalize-first">
                  {{ helper.trans('button.create')}}
                </button>
              </div>
            </div>
          </div>

        </form>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button v-if="wallet && isAdmin" type="button" @click="recreateWallet()" class="btn btn-outline-dark btn-sm text-capitalize-first me-2">
        {{ helper.trans('button.recreate')  }}
      </button>
      <button type="button" @click="account = null" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.close')  }}
      </button>

    </template>
  </b-modal>
</template>


<script lang="ts">
import {defineComponent, toRefs, reactive, computed, PropType, Prop, watch, ref} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import AccountDTO from "@/models/AccountDTO";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/components/CopyButton.vue";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {api} from "@/service/APIClient";
import WalletDTO from "@/models/WalletDTO";
import AccountWidget from "@/components/AccountWidget.vue";
import TronWeb from "tronweb";
import WalletInfoDTO from "@/models/WalletInfoDTO";
import MoneyDTO from "@/models/MoneyDTO";

const components = {
  AccountWidget,
  CopyButton,
  LoaderWrapper
}

const tronWeb = new TronWeb({
    fullHost: 'https://api.trongrid.io',
  }
)

const SUN_MUL = 1000000;
const TRC20_USDT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';

export default defineComponent({
  components,
  setup(props) {
    const store = useStore();
    const state = reactive({
      account: null as AccountDTO|null,
      wallet: null as WalletDTO|null|false,
      walletsInfo: null as WalletInfoDTO|null
    });

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const isAdmin = computed(() => store.getters.isAdmin)
    const isOperator = computed(() => store.getters.isOperator)

    const loadWalletInfo = async (wallet: WalletDTO) => {
      tronWeb.setAddress(wallet.address)

      const balanceOfResult = await tronWeb.transactionBuilder.triggerConstantContract(
        TRC20_USDT,
        'balanceOf(address)',
        {},
        [{type: 'address', value: wallet.address}],
        wallet.address
      )

      const balanceUSDT = parseInt(balanceOfResult.constant_result[0], 16) / SUN_MUL
      const account = await tronWeb.trx.getAccount(wallet.address);
      const balanceTRX = (account.balance ?? 0) / SUN_MUL

      state.walletsInfo = {
        isActivated: !!account.address,
        trx: {amount: balanceTRX.toString(), currency: 'TRX', subunit: 8} as MoneyDTO,
        usdt: {amount: balanceUSDT.toString(), currency: 'USDT', subunit: 8} as MoneyDTO
      } as WalletInfoDTO
    }

    const show = async (account: AccountDTO | null) => {
      state.account = account
      state.walletsInfo = null;

      if (!account) {
        return;
      }

      loader.value?.show()
      try {
        state.wallet = await api.accountWallet(state.account!.id);
        await loadWalletInfo(state.wallet)
      } catch (e) {
        state.wallet = false
      } finally {
        loader.value?.hide()
      }
    }

    const createWallet = async () => {
      if (!state.account) {
        return
      }

      state.wallet = null
      state.walletsInfo = null
      loader.value?.show()
      try {
        state.wallet = await api.accountWalletCreate(state.account.id);
        if (state.wallet) {
          await loadWalletInfo(state.wallet)
        }
      } catch (e) {
        state.wallet = false
      } finally {
        loader.value?.hide()
      }
    }

    // Заглушка для пересоздания кошелька
    const recreateWallet = async () => {
      if (!state.account) {
        return
      }

      state.wallet = null
      state.walletsInfo = null
      loader.value?.show()
      try {
        state.wallet = await api.accountWalletCreate(state.account.id);
        if (state.wallet) {
          await loadWalletInfo(state.wallet)
        }
      } catch (e) {
        state.wallet = false
      } finally {
        loader.value?.hide()
      }
    }

    return {
      ...toRefs(state),
      store,
      loader,
      helper,
      numberFormatter,
      isAdmin,
      isOperator,
      show,
      createWallet,
      recreateWallet,
    }
  },
})
</script>
